// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState } from 'react';

export interface ProviderLogoProps {
  /** Provider logo URL; when absent or broken, nothing is rendered. */
  logoUrl?: string | null;
  /** Provider name — drives the image alt text. */
  name: string;
  /** Square edge length in px. */
  size?: number;
  /** `circle` (fact-find cards) or `rounded` (dashboard/table avatars). */
  shape?: 'circle' | 'rounded';
  testId?: string;
}

/**
 * The provider avatar: the provider's catalogue logo when one is known, otherwise
 * **nothing**. There is deliberately NO initials-monogram fallback — a card/row
 * with no provider logo (e.g. a Dependant card, a savings account, a provider with
 * no catalogue logo) shows no avatar rather than a person-initials circle. Single
 * source of truth for the fact-find cards, the dashboard providers widget and the
 * LOA-slices table. Self-contained + design-token styled (`border-grey-05`).
 */
export function ProviderLogo({
  logoUrl,
  name,
  size = 48,
  shape = 'circle',
  testId,
}: Readonly<ProviderLogoProps>) {
  const [broken, setBroken] = useState(false);

  if (!logoUrl || broken) {
    return null;
  }

  return (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      data-testid={testId}
      onError={() => setBroken(true)}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        borderRadius: shape === 'circle' ? '50%' : '4px',
      }}
      className="shrink-0 border border-grey-05 bg-white"
    />
  );
}
